import React, { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { dxtradeCandleStream } from '@pages/Trade/Markets/Charts/utils/dxtradeCandleStream'
import { MarketDataBridge } from '@pages/Trade/Portfolio/components/MarketDataBridge'
import { DxtradeAccountMetricsProvider } from '@pages/Trade/Portfolio/context/DxtradeAccountMetricsContext'
import type { MarketDataMessage } from '@pages/Trade/Portfolio/hooks/useMarketDataWebSocket'
import { TradingAccountHeader } from '@pages/Trade/TradingAccountInfo/TradingAccountHeader'
import { AccountsModal } from '@pages/Trade/TradingAccountInfo/modals/AccountsModal'
import { useTradeLogin } from '@pages/Trade/hooks/useTradeLogin'
import { TradeExternalAccountScreen } from '@pages/Trade/shared/components/TradeExternalAccountScreen'
import { Account } from '@pages/Trade/types'

import { useModal } from '@context/ModalsContext'

import { TableNoResults } from '@ui/v2/Table/Table'

import { CommonPaths } from '@utils/path-common'

import { PlatformTypeEnum, getPlatformType } from '@model/PlatformTypeEnum'

import { Loading } from '@shared/components/Loading/Loading'

export const TradeLayout = () => {
  const tradeLogin = useTradeLogin()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedAccount, loading, setSelectedAccount, isLoggedIn, error, showAccountOpening } =
    tradeLogin
  const accountsModal = useModal(AccountsModal)

  useEffect(() => {
    if (!loading && showAccountOpening) {
      const returnPath = `${location.pathname}${location.search}`
      navigate(CommonPaths.NewLiveTradingAccount, {
        state: {
          platformType: getPlatformType(PlatformTypeEnum.TickmillTrader),
          returnPath,
        },
        replace: true,
      })
    }
  }, [loading, showAccountOpening, navigate, location.pathname, location.search])

  if (loading) {
    return <Loading isLoading showLoadingIcon />
  }

  if (!selectedAccount || !isLoggedIn || error) {
    return <TableNoResults title='Something went wrong' />
  }

  const showExternalTradeScreen = isExternalTradingPlatform(selectedAccount)

  return (
    <DxtradeAccountMetricsProvider>
      <MarketDataBridge onTick={handleMarketTick} />

      <TradingAccountHeader
        account={selectedAccount}
        onClick={() =>
          accountsModal.open({
            selectedAccount,
            onAccountChange: setSelectedAccount,
          })
        }
      />
      {showExternalTradeScreen ? (
        <TradeExternalAccountScreen
          key={selectedAccount.id}
          account={selectedAccount}
          isError={!!error}
        />
      ) : (
        <Outlet context={tradeLogin} />
      )}
    </DxtradeAccountMetricsProvider>
  )
}

const isExternalTradingPlatform = (account: Account) =>
  [PlatformTypeEnum.MT4, PlatformTypeEnum.MT5].includes(account.platformType.id)

const handleMarketTick = (message: MarketDataMessage) => dxtradeCandleStream.handleMessage(message)
